/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import { PageSkeleton, SectionLabel } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, riseAt } from '../../components/common/ui-kit';
import FormSelect from '../../components/forms/form-select';
import FormMultiSelect from '../../components/forms/form-multi-select';
import SourcePicker from '../../components/forms/source-picker';

import { evalCasesService } from '../../services/eval-cases.service';
import { agentsService } from '../../services/agents.service';
import { projectsService } from '../../services/projects.service';
import { valueSetsService } from '../../services/value-sets.service';
import { extractErrorMessage } from '../../utils/error-handler';

const TYPE_OPTIONS = [
  { label: 'Agent', value: 'agent' },
  { label: 'Prompt', value: 'prompt' },
  { label: 'RAG', value: 'rag' },
  { label: 'Model', value: 'model' },
];

// What the test IS. Separate from `type` (what can run it) so one Q&A case can be
// reused by a prompt suite and a rag suite instead of being duplicated per target.
const CASE_TYPE_OPTIONS = [
  { label: 'Question / Answer', value: 'QUESTION_ANSWER' },
  { label: 'Structured Extraction', value: 'STRUCTURED_EXTRACTION' },
  { label: 'Agent Task', value: 'AGENT_TASK' },
];

const caseSchema = z.object({
  caseCode: z.string().min(1, 'Case code is required').max(150).trim(),
  name: z.string().min(1, 'Name is required').max(200).trim(),
  type: z.array(z.string()).min(1, 'Pick at least one target type'),
  caseType: z.string().optional().or(z.literal('')),  // test shape (evalCaseType)
  description: z.string().max(1000).optional().or(z.literal('')),
  version: z.number().nullable().optional(),
  // Required: a case with no input fails every run with RUN.CASE_INPUT_EMPTY, so
  // it is refused here rather than saved and discovered later.
  input: z.string().trim().min(1, 'The question or task is required'),
  expectedResult: z.string().optional().or(z.literal('')),
  requiredFacts: z.string().optional().or(z.literal('')),    // one per line in the form
  // Picked from real documents, never typed — see SourcePicker.
  expectedSources: z.array(z.string()),
  context: z.string().optional().or(z.literal('')),  // one passage per blank line
  // Agent cases. The picker covers the common case (which tools, no argument
  // expectation); the JSON fields are the advanced path, for arguments.
  expectedToolNames: z.array(z.string()),
  tools: z.string().optional().or(z.literal('')),
  expectedToolCalls: z.string().optional().or(z.literal('')),
  tags: z.string().optional().or(z.literal('')),  // comma-separated in the form
  appId: z.string().trim().optional().or(z.literal('')),
});

type CaseFormData = z.infer<typeof caseSchema>;

const EvalCaseForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'eval-case', basePath: '/eval-cases' });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const {
    control, handleSubmit, reset, watch, setValue,
    formState: { errors, isDirty },
  } = useForm<CaseFormData>({
    resolver: zodResolver(caseSchema),
    defaultValues: {
      caseCode: '', name: '', type: [], caseType: '', description: '', version: 1,
      input: '', expectedResult: '', requiredFacts: '', expectedSources: [], context: '',
      expectedToolNames: [], tools: '', expectedToolCalls: '', tags: '', appId: '',
    },
    mode: 'onChange',
  });

  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) setValue('appId', accessibleProjects[0]._id);
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  const { data: typeVS } = useQuery({
    queryKey: ['value-sets', 'evalType'],
    queryFn: () => valueSetsService.getByKey('evalType', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  const typeOptions = typeVS?.items?.length ? typeVS.items.map((i) => ({ value: i.code, label: i.label })) : TYPE_OPTIONS;

  const { data: caseTypeVS } = useQuery({
    queryKey: ['value-sets', 'evalCaseType'],
    queryFn: () => valueSetsService.getByKey('evalCaseType', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  // The value set is authoritative; the built-in list keeps the field usable on
  // a database where it was never seeded.
  const caseTypeOptions = caseTypeVS?.items?.length
    ? caseTypeVS.items.map((i) => ({ value: i.code, label: i.label }))
    : CASE_TYPE_OPTIONS;

  // What the case runs on decides which of its fields mean anything. A RAG case
  // is judged on the documents it should cite; an agent case on the tools it
  // reaches for; a prompt case on its answer alone. Showing every field for
  // every case is how a prompt case ended up with expected sources nothing
  // would ever compare.
  const runsOn = watch('type') || [];
  const isRag = runsOn.includes('rag');
  const isAgent = runsOn.includes('agent');
  const isPrompt = runsOn.includes('prompt');
  const answersInText = isRag || isPrompt;

  const [agentId, setAgentId] = useState('');
  const [advancedTools, setAdvancedTools] = useState(false);
  const { data: agentsResp } = useQuery({
    queryKey: ['agents', 'picker'],
    queryFn: () => agentsService.list({ page: 1, page_size: 100 }),
    enabled: isAgent,
    staleTime: 5 * 60 * 1000,
  });
  const agents = agentsResp?.data || [];
  const agentOptions = agents.map((a) => ({ value: a._id, label: `${a.name} (${a.agentCode})` }));
  const toolOptions = useMemo(() => {
    const a = agents.find((x) => x._id === agentId);
    const names = (a?.tools || []).map((t: any) => t?.name).filter(Boolean) as string[];
    // Keep a name the case already expects even if this agent no longer offers
    // it, so editing never silently drops an expectation.
    for (const n of watch('expectedToolNames') || []) if (!names.includes(n)) names.push(n);
    return names.map((n) => ({ value: n, label: n }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, agentId, watch('expectedToolNames')]);

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const { data: caseData, isLoading: fetchLoading } = useQuery({
    queryKey: ['eval-cases', id],
    queryFn: () => evalCasesService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  useBreadcrumbTitle(isEditMode ? caseData?.name : null);

  const asText = (v: unknown): string =>
    v === null || v === undefined ? '' : typeof v === 'string' ? v : JSON.stringify(v, null, 2);
  /** The edited text, back in the shape it was stored in: parsed JSON when the
   *  stored value was an object/array and the text still parses; the text otherwise. */
  const keepShape = (original: unknown, edited?: string | null): unknown => {
    if (!edited) return null;
    if (original !== null && typeof original === 'object') {
      try { return JSON.parse(edited); } catch { /* the author made it plain text */ }
    }
    return edited;
  };

  useEffect(() => {
    if (caseData && isEditMode) {
      reset({
        caseCode: caseData.caseCode || '',
        name: caseData.name || '',
        // Tolerates a pre-migration row whose type is still a bare string.
        type: Array.isArray(caseData.type) ? caseData.type : caseData.type ? [caseData.type] : [],
        caseType: caseData.caseType || '',
        description: caseData.description || '',
        version: caseData.version ?? 1,
        input: asText(caseData.input),
        expectedResult: asText(caseData.expectedResult),
        requiredFacts: (caseData.requiredFacts || []).join('\n'),
        expectedSources: (caseData.expectedSources || [])
          .map((x: any) => (typeof x === 'string' ? x : x?.sourceRef))
          .filter(Boolean),
        context: (caseData.context || []).join('\n\n'),
        expectedToolNames: (caseData.expectedToolCalls || []).map((c: any) => c?.name).filter(Boolean),
        tools: caseData.tools?.length ? JSON.stringify(caseData.tools, null, 2) : '',
        expectedToolCalls: caseData.expectedToolCalls?.length ? JSON.stringify(caseData.expectedToolCalls, null, 2) : '',
        tags: (caseData.tags || []).join(', '),
        appId: (caseData as any).appId || '',
      });
      // A case that expects ARGUMENTS, or carries its own tool list, can only be
      // expressed in JSON — open that view rather than hide what it asserts.
      const carriesArgs = (caseData.expectedToolCalls || []).some((c: any) => c?.arguments != null);
      setAdvancedTools(carriesArgs || !!caseData.tools?.length);
    }
  }, [caseData, isEditMode, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEditMode && id ? evalCasesService.update(id, data) : evalCasesService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-cases'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Eval case ${isEditMode ? 'updated' : 'created'} successfully`, life: 3000 });
      setTimeout(() => navigateToList(), 1000);
    },
    onError: (error: any) => {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} eval case`), life: 10000 });
    },
  });

  const isSaving = mutation.isPending;

  // A JSON textarea either parses to an array or the save is refused with the
  // parse error — silently dropping a malformed tool list would make an agent
  // case look saved while grading against nothing.
  const parseJsonArray = (raw: string | undefined, label: string): unknown[] | null => {
    const text = (raw || '').trim();
    if (!text) return null;
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) throw new Error(`${label} must be a JSON array`);
    return parsed;
  };

  const onSubmit = (data: CaseFormData) => {
    const lines = (s?: string) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);

    let tools: unknown[] | null = null;
    let expectedToolCalls: unknown[] | null = null;
    if (isAgent) {
      if (advancedTools) {
        try {
          tools = parseJsonArray(data.tools, 'Tools');
          expectedToolCalls = parseJsonArray(data.expectedToolCalls, 'Expected tool calls');
        } catch (e: any) {
          toast.current?.show({ severity: 'error', summary: 'Invalid JSON', detail: e?.message || String(e), life: 8000 });
          return;
        }
      } else {
        // No argument expectation: ARGUMENT_CORRECTNESS judges the arguments
        // against the input instead, which is usually the better test.
        expectedToolCalls = data.expectedToolNames.length
          ? data.expectedToolNames.map((name) => ({ name, arguments: null, order: null }))
          : null;
      }
    }
    const payload: any = {
      caseCode: data.caseCode,
      name: data.name,
      type: data.type,
      caseType: data.caseType || null,
      description: data.description || null,
      version: data.version ?? 1,
      // A case whose input/expectation was STRUCTURED (e.g. an agent's {task: …})
      // is shown as pretty JSON; sending that text back turned it into a string on
      // every save, and the agent was then handed the whole JSON blob as its task.
      input: keepShape(caseData?.input, data.input),
      expectedResult: keepShape(caseData?.expectedResult, data.expectedResult),
      // A section that is not shown is not saved. Unticking RAG used to leave the
      // sources in the form and send them anyway, so the case kept asserting
      // citations nothing would ever check.
      requiredFacts: answersInText ? lines(data.requiredFacts) : [],
      // Bare strings: the service normalises them to [{sourceRef}].
      expectedSources: isRag ? data.expectedSources : [],
      // Blank-line separated: a passage is usually several lines of its own.
      context: answersInText
        ? (data.context || '').split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean)
        : [],
      tools,
      expectedToolCalls,
      tags: (data.tags || '').split(',').map((t) => t.trim()).filter(Boolean),
    };
    if (!isEditMode && data.appId) payload.appId = data.appId;
    mutation.mutate(payload);
  };

  if (isEditMode && fetchLoading) return <PageSkeleton panels={2} />;

  return (
    <div className="p-4" data-testid="SCR-EvalCase-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader
        icon="pi pi-check-square"
        onBack={() => handleNavigate('/eval-cases')}
        title={isEditMode ? 'Edit Eval Case' : 'Create Eval Case'}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 w-full">
        <div className={`${PANEL} ui-rise p-4`} style={riseAt(0)}>
          <SectionLabel icon="pi pi-info-circle" className="mb-3">Identity</SectionLabel>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Controller name="caseCode" control={control} render={({ field }) => (
            <Input label="Case Code *" {...field} error={errors.caseCode?.message} placeholder="case_bns_robbery_001" disabled={isEditMode} />
          )} />
          <Controller name="name" control={control} render={({ field }) => (
            <Input label="Name *" {...field} error={errors.name?.message} placeholder="Robbery definition" />
          )} />
          <Controller name="type" control={control} render={({ field }) => (
            <FormMultiSelect label="Runs On *" name={field.name} value={field.value || []} options={typeOptions} onChange={field.onChange} error={!!errors.type} helperText={errors.type?.message || 'Target types this case can be evaluated against'} />
          )} />
          {/* One field, not two. Both a hardcoded list and the value set used to be
              rendered here, bound to the same `caseType`. */}
          <Controller name="caseType" control={control} render={({ field }) => (
            <FormSelect label="Case type" name={field.name} value={field.value || ''} options={caseTypeOptions} onChange={field.onChange} showClear helperText="What the test is. Derived from Runs On when left blank." />
          )} />
          <Controller name="version" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Version</label>
              <InputNumber value={field.value ?? 1} onValueChange={(e) => field.onChange(e.value ?? 1)} min={1} useGrouping={false} className="w-full" />
            </div>
          )} />
          {!isEditMode && (
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''} options={projectOptions} onChange={field.onChange} disabled={projectLocked} />
            )} />
          )}
          <Controller name="tags" control={control} render={({ field }) => (
            <div className="sm:col-span-2">
              <Input label="Tags (comma-separated)" {...field} placeholder="BNS, BASELINE" />
            </div>
          )} />
          <Controller name="description" control={control} render={({ field }) => (
            <div className="sm:col-span-2">
              <TextArea label="Description" {...field} rows={2} />
            </div>
          )} />
          </div>
        </div>

        {runsOn.length === 0 ? (
          <div className={`${PANEL} ui-rise p-4`} style={riseAt(1)}>
            <SectionLabel icon="pi pi-flag" className="mb-2">The test</SectionLabel>
            <p className="text-sm text-ink-muted">
              Choose what this case <span className="font-medium">Runs On</span> above. A prompt case is judged on its
              answer, a RAG case also on the documents it cites, and an agent case on the tools it calls — each shows
              only the fields it is graded on.
            </p>
          </div>
        ) : (
          <div className={`${PANEL} ui-rise p-4`} style={riseAt(1)}>
            <SectionLabel icon="pi pi-flag" className="mb-3">The test</SectionLabel>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Controller name="input" control={control} render={({ field }) => (
                <TextArea label={isAgent && !answersInText ? 'Task *' : 'Question / input *'} {...field} rows={5}
                  error={errors.input?.message}
                  placeholder={isAgent && !answersInText
                    ? 'What happens at the desk — the situation the agent has to act on'
                    : 'The question exactly as a user would ask it'} />
              )} />
              <Controller name="expectedResult" control={control} render={({ field }) => (
                <TextArea label={isAgent && !answersInText ? 'Expected outcome (optional)' : 'Expected answer'} {...field} rows={5}
                  placeholder={isAgent && !answersInText
                    ? 'Only if the agent should also SAY something — the calls below are what it is graded on'
                    : 'The fact the answer must convey. Exact / contains graders compare against this text.'} />
              )} />
            </div>
          </div>
        )}

        {answersInText && (
          <div className={`${PANEL} ui-rise p-4`} style={riseAt(2)} data-testid="EvalCaseForm.Section.Answer">
            <SectionLabel icon="pi pi-check-square" className="mb-1">Answer checks</SectionLabel>
            <p className="mb-3 text-xs text-ink-muted">
              {isRag && isPrompt ? 'Prompt and RAG' : isRag ? 'RAG' : 'Prompt'} · both optional
            </p>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Controller name="requiredFacts" control={control} render={({ field }) => (
                <TextArea label="Required facts (one per line)" {...field} rows={4}
                  placeholder={'Robbery involves theft\nForce or threat is used'} />
              )} />
              {/* Ground truth the case carries, independent of what the retriever
                  found. FACTUAL_CONSISTENCY checks the answer against this; groundedness against
                  retrieved passages alone cannot catch a confidently wrong fetch. */}
              <Controller name="context" control={control} render={({ field }) => (
                <TextArea label="Ground truth (blank line between passages)" {...field} rows={4}
                  placeholder={'BNS 2023, s.304(2): whoever commits snatching shall be punished with\nimprisonment which may extend to three years.'} />
              )} />
            </div>
            <p className="mt-2 text-xs text-ink-muted">
              The AI judge checks the answer states each required fact (ANSWER_CORRECTNESS deducts for any it misses). Ground truth is what Factual consistency checks the answer against — in a suite that scores it, a case without ground truth is marked errored.
            </p>
          </div>
        )}

        {isRag && (
          <div className={`${PANEL} ui-rise p-4`} style={riseAt(3)} data-testid="EvalCaseForm.Section.Rag">
            <SectionLabel icon="pi pi-book" className="mb-3">Retrieval · RAG</SectionLabel>
            <Controller name="expectedSources" control={control} render={({ field }) => (
              <SourcePicker value={field.value || []} onChange={field.onChange} />
            )} />
          </div>
        )}

        {isAgent && (
          <div className={`${PANEL} ui-rise p-4`} style={riseAt(4)} data-testid="EvalCaseForm.Section.Agent">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <SectionLabel icon="pi pi-wrench">Tool calls · Agent</SectionLabel>
              <button type="button" onClick={() => setAdvancedTools((v) => !v)}
                className="ui-press text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                data-testid="EvalCaseForm.ToggleAdvancedTools">
                {advancedTools ? 'Use the tool picker' : 'Expect specific arguments (JSON)'}
              </button>
            </div>
            {!advancedTools ? (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <FormSelect label="Tools from agent" name="toolAgent" value={agentId} options={agentOptions}
                  onChange={(e: any) => setAgentId(e?.target?.value ?? '')} showClear filter
                  helperText="The agent whose tool list to choose from. Not saved on the case." />
                <Controller name="expectedToolNames" control={control} render={({ field }) => (
                  <FormMultiSelect label="Expected tool calls" name={field.name} value={field.value || []}
                    options={toolOptions} onChange={field.onChange}
                    helperText={agentId || field.value?.length
                      ? 'Scored by TOOL_CALLS. Arguments are judged against the task by ARGUMENT_CORRECTNESS.'
                      : 'Choose an agent first'} />
                )} />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {/* `tools` is what the model is OFFERED; `expectedToolCalls` is what it
                    should reach for. Both provider-neutral JSON. */}
                <Controller name="expectedToolCalls" control={control} render={({ field }) => (
                  <TextArea label="Expected tool calls (JSON array)" {...field} rows={4}
                    placeholder={'[{"name": "register_zero_fir", "arguments": {"placeOfOffence": "Vijayawada"}}]'} />
                )} />
                <Controller name="tools" control={control} render={({ field }) => (
                  <TextArea label="Tools offered (JSON array — leave empty to use the agent's own)" {...field} rows={4}
                    placeholder={'[{"name": "lookup_case", "description": "Fetch a case by id", "parameters": {"type": "object"}}]'} />
                )} />
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <span className="ui-press inline-flex"><Button type="button" label="Cancel" severity="secondary" outlined onClick={() => handleNavigate('/eval-cases')} /></span>
          <span className="ui-press inline-flex">
            <Button type="submit" label={isSaving ? 'Saving…' : isEditMode ? 'Update Case' : 'Create Case'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={isSaving} />
          </span>
        </div>
      </form>

      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave} />
    </div>
  );
};

export default EvalCaseForm;
